import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Navigation } from '../../home/components/Navigation';
import { Footer } from '../../home/components/Footer';
import { ServiceModal } from '../../home/components/ServiceModal';
import { IndustriesModal } from '../../home/components/IndustriesModal';
import { HowItWorksModal } from '../../home/components/HowItWorksModal';
import { CalendarModal } from '../../home/components/CalendarModal';
import { SEO } from '../../../components/SEO';

const faqs = [
  {
    question: 'What is marketing automation for small businesses?',
    answer: 'Marketing automation for small businesses uses AI and software to automatically execute repetitive marketing tasks — sending follow-up emails, nurturing leads, posting to social media, running drip campaigns, and re-engaging past customers — without manual effort. It lets you run enterprise-level marketing on a small business budget and schedule.'
  },
  {
    question: 'Do I need a big marketing budget to use marketing automation?',
    answer: 'Not at all. Marketing automation is actually most valuable for small businesses with limited budgets, because it multiplies the impact of every dollar you spend. Instead of paying for ads that reach cold audiences, automation nurtures the leads you already have — turning more of them into paying customers without additional ad spend.'
  },
  {
    question: 'What marketing tasks can be automated?',
    answer: 'A wide range: follow-up email sequences, SMS drip campaigns, review request messages, appointment reminders, re-engagement campaigns for past customers, social media scheduling, lead nurturing workflows, birthday or anniversary messages, and post-service check-ins. Essentially any repetitive, time-based marketing touchpoint can be automated.'
  },
  {
    question: 'Will automated marketing messages feel generic or spammy?',
    answer: 'Only if they\'re done poorly. Our system personalizes every message with the recipient\'s name, their specific service history, and relevant context. Messages are sent at the right time based on customer behavior — not blasted to everyone at once. Done right, customers often don\'t realize the message was automated.'
  },
  {
    question: 'How does marketing automation help with customer retention?',
    answer: 'Retention is where automation really shines. The system automatically follows up after every job, requests reviews, sends seasonal promotions to past customers, and re-engages anyone who hasn\'t booked in a while. Most small businesses see a 20–35% increase in repeat business within the first 90 days of using automated retention campaigns.'
  },
  {
    question: 'Can marketing automation help me get more Google reviews?',
    answer: 'Yes — and this is one of the highest-ROI automations we set up. After every completed job, the system automatically sends a review request via text or email with a direct link to your Google Business Profile. Businesses using automated review requests typically see 3–5x more reviews within 60 days, which directly improves local search rankings.'
  },
  {
    question: 'How does it integrate with my existing tools?',
    answer: 'Our marketing automation integrates with your CRM, email platform, SMS provider, Google Business Profile, Facebook, and scheduling tools. Customer data flows automatically between systems, so your campaigns are always based on accurate, up-to-date information without manual syncing.'
  },
  {
    question: 'How quickly can I see results from marketing automation?',
    answer: 'Most clients see measurable results within 30–60 days. Review counts typically increase within the first 2–3 weeks. Lead nurturing campaigns start converting within the first month. Repeat customer campaigns show results within 60–90 days as past customers re-engage. The longer the system runs, the more powerful it becomes.'
  }
];

const stats = [
  { value: '451%', label: 'Average increase in qualified leads from automation' },
  { value: '3–5x', label: 'More Google reviews with automated requests' },
  { value: '20%', label: 'Average increase in repeat customer bookings' },
  { value: '14 hrs', label: 'Per week saved on manual marketing tasks' },
];

const features = [
  {
    icon: 'ri-mail-send-line',
    title: 'Email Drip Campaigns',
    description: 'Automatically nurture leads and past customers with personalized email sequences triggered by their behavior, timeline, or service history.'
  },
  {
    icon: 'ri-message-3-line',
    title: 'SMS Marketing Automation',
    description: 'Send timely, personalized text messages for follow-ups, promotions, appointment reminders, and re-engagement — with 98% open rates.'
  },
  {
    icon: 'ri-star-line',
    title: 'Automated Review Requests',
    description: 'After every completed job, automatically ask happy customers for a Google review with a direct link. Get 3–5x more reviews without lifting a finger.'
  },
  {
    icon: 'ri-repeat-2-line',
    title: 'Re-Engagement Campaigns',
    description: 'Automatically reach out to past customers who haven\'t booked in 60, 90, or 180 days with personalized offers to bring them back.'
  },
  {
    icon: 'ri-calendar-event-line',
    title: 'Appointment Reminders',
    description: 'Reduce no-shows with automated reminder sequences sent 24 hours and 1 hour before every appointment via text and email.'
  },
  {
    icon: 'ri-instagram-line',
    title: 'Social Media Scheduling',
    description: 'Plan and schedule posts across Facebook and Instagram in advance. Maintain a consistent presence without daily manual posting.'
  },
  {
    icon: 'ri-user-follow-line',
    title: 'Lead Nurture Workflows',
    description: 'Automatically move leads through a personalized journey from first contact to booked appointment with multi-step nurture sequences.'
  },
  {
    icon: 'ri-bar-chart-box-line',
    title: 'Campaign Analytics',
    description: 'Track open rates, click rates, conversion rates, and revenue attributed to each campaign. Know exactly what\'s working and what isn\'t.'
  }
];

const useCases = [
  {
    title: 'New Lead Nurture',
    description: 'A new lead fills out your form. The system immediately sends a welcome text, follows up with an email the next day, and continues a 7-day sequence until they book.',
    icon: 'ri-user-add-line',
    result: 'Convert 40% more leads without any manual follow-up'
  },
  {
    title: 'Post-Job Review Request',
    description: 'A job is marked complete in your system. 2 hours later, the customer gets a personalized text asking for a Google review with a direct link.',
    icon: 'ri-star-line',
    result: 'Get 3–5x more reviews in the first 60 days'
  },
  {
    title: 'Seasonal Re-Engagement',
    description: 'Every spring, past landscaping customers automatically receive a personalized message about spring cleanup services with a special returning-customer offer.',
    icon: 'ri-sun-line',
    result: '20–35% increase in repeat bookings'
  },
  {
    title: 'No-Show Prevention',
    description: 'Every booked appointment triggers an automatic reminder sequence — 24 hours before, 2 hours before, and a same-day confirmation — reducing no-shows dramatically.',
    icon: 'ri-calendar-check-line',
    result: 'Reduce no-shows by up to 50%'
  }
];

export default function MarketingAutomationSmallBusiness() {
  const [isServiceModalOpen, setIsServiceModalOpen] = useState(false);
  const [selectedService, setSelectedService] = useState('');
  const [isIndustriesModalOpen, setIsIndustriesModalOpen] = useState(false);
  const [isHowItWorksModalOpen, setIsHowItWorksModalOpen] = useState(false);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);

  const handleOpenService = (title: string) => {
    setSelectedService(title);
    setIsServiceModalOpen(true);
  };

  return (
    <>
      <SEO
        title="Marketing Automation for Small Businesses | Dino Nuggets"
        description="Automate your marketing and get more leads, reviews, and repeat customers — without more ad spend. Email, SMS, review requests, and re-engagement campaigns on autopilot."
        canonical="/services/marketing-automation-small-business"
        schemas={[
          {
            type: 'Service',
            data: {
              '@type': 'Service',
              name: 'Marketing Automation for Small Businesses',
              description: 'AI-powered marketing automation for small businesses including email drip campaigns, SMS marketing, automated review requests, and customer re-engagement workflows.',
              provider: {
                '@type': 'Organization',
                name: 'Dino Nuggets',
                url: import.meta.env.VITE_SITE_URL || 'https://dinonuggets.ai'
              },
              areaServed: 'United States',
              availableChannel: {
                '@type': 'ServiceChannel',
                serviceUrl: `${import.meta.env.VITE_SITE_URL || 'https://dinonuggets.ai'}/services/marketing-automation-small-business`
              }
            }
          },
          {
            type: 'FAQPage',
            data: {
              '@type': 'FAQPage',
              mainEntity: faqs.map(faq => ({
                '@type': 'Question',
                name: faq.question,
                acceptedAnswer: { '@type': 'Answer', text: faq.answer }
              }))
            }
          }
        ]}
      />

      <div className="min-h-screen bg-white">
        <Navigation />

        {/* Hero */}
        <section className="relative pt-32 pb-24 px-6 lg:px-8 overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="/readdy-assets/mktg-auto-hero-001.jpg"
              alt="Marketing Automation for Small Businesses"
              className="w-full h-full object-cover object-top"
            />
          </div>
          <div className="absolute inset-0 bg-gradient-to-b from-black/65 via-black/50 to-black/70"></div>

          <div className="relative w-full max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-primary/20 border border-primary/40 text-primary px-4 py-2 rounded-full text-sm font-semibold mb-6">
              <i className="ri-rocket-line"></i>
              More leads, reviews &amp; repeat customers — on autopilot
            </div>
            <h1 className="font-display text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
              Marketing Automation for Small Businesses
            </h1>
            <p className="text-xl text-white/85 mb-10 leading-relaxed max-w-3xl mx-auto">
              Stop doing marketing manually. Our AI-powered automation handles your follow-ups, review requests, re-engagement campaigns, and appointment reminders — so you get more customers without more work.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => setIsCalendarOpen(true)}
                className="bg-primary hover:bg-primary/90 text-white px-8 py-4 rounded-lg font-semibold transition-all whitespace-nowrap cursor-pointer"
              >
                Automate My Marketing
              </button>
              <button
                onClick={() => setIsCalendarOpen(true)}
                className="bg-white/10 hover:bg-white/20 border border-white/30 text-white px-8 py-4 rounded-lg font-semibold transition-all whitespace-nowrap cursor-pointer"
              >
                See What Gets Automated
              </button>
            </div>
          </div>
        </section>

        {/* Stats Bar */}
        <section className="bg-secondary py-12 px-6 lg:px-8">
          <div className="max-w-5xl mx-auto grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat) => (
              <div key={stat.label} className="text-center">
                <div className="text-3xl lg:text-4xl font-bold text-primary mb-2">{stat.value}</div>
                <div className="text-gray-400 text-sm leading-snug">{stat.label}</div>
              </div>
            ))}
          </div>
        </section>

        {/* Main Content */}
        <section className="py-20 px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">

            {/* What Is It */}
            <div className="mb-20">
              <h2 className="font-display text-3xl font-bold text-gray-900 mb-6">
                What Is Marketing Automation for Small Businesses?
              </h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Marketing automation uses AI and software to automatically execute your marketing campaigns — sending the right message to the right person at the right time, without you having to do it manually every single time. When paired with <Link to="/services/ai-lead-follow-up" className="text-primary hover:underline font-medium">AI lead follow-up</Link>, your entire funnel from first contact to booked job runs hands-free.
              </p>
              <p className="text-gray-700 leading-relaxed mb-4">
                For small businesses, this is a game-changer. You don't have a marketing department. You don't have time to send follow-up emails, request reviews after every job, re-engage past customers, or post consistently on social media. But your competitors who do these things consistently are winning more business than you.
              </p>
              <p className="text-gray-700 leading-relaxed">
                Marketing automation levels the playing field. Once set up, your campaigns run automatically in the background — nurturing leads, collecting reviews, bringing back past customers, and keeping your business top-of-mind — while you focus on actually doing the work. Combine it with <Link to="/services/automated-customer-service" className="text-primary hover:underline font-medium">automated customer service</Link> and your entire customer experience becomes seamless. It's like having a full-time marketing team working 24/7 for a fraction of the cost.
              </p>
            </div>

            {/* Use Cases */}
            <div className="mb-20">
              <h2 className="font-display text-3xl font-bold text-gray-900 mb-4">
                Real-World Automation Examples
              </h2>
              <p className="text-gray-600 mb-10 leading-relaxed">
                Here's exactly what marketing automation looks like in practice for a small service business:
              </p>
              <div className="grid md:grid-cols-2 gap-6">
                {useCases.map((useCase) => (
                  <div key={useCase.title} className="border border-gray-200 p-6 rounded-xl hover:border-primary/40 transition-colors">
                    <div className="flex items-start gap-4 mb-4">
                      <div className="w-10 h-10 flex items-center justify-center bg-primary/10 rounded-lg flex-shrink-0">
                        <i className={`${useCase.icon} text-xl text-primary`}></i>
                      </div>
                      <h3 className="font-semibold text-gray-900 text-lg">{useCase.title}</h3>
                    </div>
                    <p className="text-gray-600 text-sm leading-relaxed mb-4">{useCase.description}</p>
                    <div className="flex items-center gap-2 bg-primary/5 border border-primary/20 px-4 py-2 rounded-full w-fit">
                      <i className="ri-arrow-up-line text-primary text-sm"></i>
                      <span className="text-primary text-xs font-semibold">{useCase.result}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Features Grid */}
            <div className="mb-20">
              <h2 className="font-display text-3xl font-bold text-gray-900 mb-4">
                What Gets Automated
              </h2>
              <p className="text-gray-600 mb-10 leading-relaxed">
                A complete marketing automation system built for small service businesses:
              </p>
              <div className="grid md:grid-cols-2 gap-6">
                {features.map((feature) => (
                  <div key={feature.title} className="border border-gray-200 p-6 rounded-lg hover:border-primary/40 transition-colors">
                    <div className="w-10 h-10 flex items-center justify-center bg-primary/10 rounded-lg mb-4">
                      <i className={`${feature.icon} text-xl text-primary`}></i>
                    </div>
                    <h3 className="font-semibold text-gray-900 mb-2">{feature.title}</h3>
                    <p className="text-gray-600 text-sm leading-relaxed">{feature.description}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Why It Matters */}
            <div className="mb-20 bg-gradient-to-br from-gray-50 to-primary/5 p-10 rounded-2xl">
              <h2 className="font-display text-3xl font-bold text-gray-900 mb-6">
                Why Most Small Businesses Lose to Competitors
              </h2>
              <p className="text-gray-700 leading-relaxed mb-6">
                It's rarely about having a better product or service. Most small businesses lose customers to competitors because of one thing: <strong>consistency</strong>. The business that follows up faster, asks for reviews more often, and stays top-of-mind between jobs wins — even if their work isn't better than yours.
              </p>
              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div className="bg-white p-6 rounded-xl border border-gray-200">
                  <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <i className="ri-close-circle-line text-red-400"></i>
                    Without Automation
                  </h3>
                  <ul className="space-y-2 text-sm text-gray-600">
                    <li className="flex items-start gap-2"><i className="ri-subtract-line text-red-400 mt-0.5 flex-shrink-0"></i>Leads go cold because follow-up is inconsistent</li>
                    <li className="flex items-start gap-2"><i className="ri-subtract-line text-red-400 mt-0.5 flex-shrink-0"></i>Happy customers forget to leave reviews</li>
                    <li className="flex items-start gap-2"><i className="ri-subtract-line text-red-400 mt-0.5 flex-shrink-0"></i>Past customers book competitors when they need service again</li>
                    <li className="flex items-start gap-2"><i className="ri-subtract-line text-red-400 mt-0.5 flex-shrink-0"></i>Hours wasted on manual emails and messages</li>
                  </ul>
                </div>
                <div className="bg-white p-6 rounded-xl border border-primary/30">
                  <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <i className="ri-checkbox-circle-line text-primary"></i>
                    With Automation
                  </h3>
                  <ul className="space-y-2 text-sm text-gray-600">
                    <li className="flex items-start gap-2"><i className="ri-check-line text-primary mt-0.5 flex-shrink-0"></i>Every lead gets followed up within 60 seconds, every time</li>
                    <li className="flex items-start gap-2"><i className="ri-check-line text-primary mt-0.5 flex-shrink-0"></i>Review requests go out automatically after every job</li>
                    <li className="flex items-start gap-2"><i className="ri-check-line text-primary mt-0.5 flex-shrink-0"></i>Past customers get re-engaged before they think to call a competitor</li>
                    <li className="flex items-start gap-2"><i className="ri-check-line text-primary mt-0.5 flex-shrink-0"></i>Marketing runs 24/7 with zero manual effort</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Related Services */}
            <div className="mb-20 bg-gradient-to-br from-gray-50 to-primary/5 p-8 rounded-xl">
              <h2 className="font-display text-2xl font-bold text-gray-900 mb-4">
                Complete the Automation Picture
              </h2>
              <p className="text-gray-600 mb-6 leading-relaxed">
                Marketing automation works best as part of a full AI stack. Pair it with:
              </p>
              <div className="grid md:grid-cols-3 gap-4">
                <Link
                  to="/services/ai-lead-follow-up"
                  className="bg-white p-6 rounded-lg border border-gray-200 hover:border-primary hover:shadow-md transition-all cursor-pointer group"
                >
                  <i className="ri-send-plane-line text-3xl text-primary mb-3 block"></i>
                  <h3 className="font-semibold text-gray-900 mb-2 group-hover:text-primary transition-colors">
                    AI Lead Follow-Up
                  </h3>
                  <p className="text-sm text-gray-600">
                    Respond to every new lead in 60 seconds and nurture them to a booking automatically.
                  </p>
                </Link>
                <Link
                  to="/services/automated-customer-service"
                  className="bg-white p-6 rounded-lg border border-gray-200 hover:border-primary hover:shadow-md transition-all cursor-pointer group"
                >
                  <i className="ri-customer-service-2-line text-3xl text-primary mb-3 block"></i>
                  <h3 className="font-semibold text-gray-900 mb-2 group-hover:text-primary transition-colors">
                    Automated Customer Service
                  </h3>
                  <p className="text-sm text-gray-600">
                    Handle 85% of customer inquiries automatically via chat, SMS, and email.
                  </p>
                </Link>
                <Link
                  to="/services/ai-chatbot"
                  className="bg-white p-6 rounded-lg border border-gray-200 hover:border-primary hover:shadow-md transition-all cursor-pointer group"
                >
                  <i className="ri-chat-smile-3-line text-3xl text-primary mb-3 block"></i>
                  <h3 className="font-semibold text-gray-900 mb-2 group-hover:text-primary transition-colors">
                    AI Chatbot
                  </h3>
                  <p className="text-sm text-gray-600">
                    Capture leads from your website 24/7 and feed them into your marketing pipeline.
                  </p>
                </Link>
              </div>
            </div>

            {/* FAQ */}
            <div className="mb-20">
              <h2 className="font-display text-3xl font-bold text-gray-900 mb-8">
                Frequently Asked Questions
              </h2>
              <div className="space-y-4">
                {faqs.map((faq, index) => (
                  <div key={index} className="border border-gray-200 rounded-lg overflow-hidden">
                    <button
                      onClick={() => setOpenFaqIndex(openFaqIndex === index ? null : index)}
                      className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors cursor-pointer"
                    >
                      <h3 className="font-semibold text-gray-900 pr-4">{faq.question}</h3>
                      <i className={`ri-arrow-down-s-line text-2xl text-gray-400 flex-shrink-0 transition-transform ${openFaqIndex === index ? 'rotate-180' : ''}`}></i>
                    </button>
                    {openFaqIndex === index && (
                      <div className="px-6 pb-4 pt-2">
                        <p className="text-gray-700 leading-relaxed">{faq.answer}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* CTA */}
            <div className="bg-gradient-to-br from-primary to-secondary text-white p-12 rounded-2xl text-center">
              <h2 className="font-display text-3xl font-bold mb-4">
                Put Your Marketing on Autopilot
              </h2>
              <p className="text-lg mb-8 text-white/90 max-w-2xl mx-auto">
                More reviews, more repeat customers, more leads — without more work. Get your marketing automation set up in 48 hours and start seeing results within 30 days.
              </p>
              <button
                onClick={() => setIsCalendarOpen(true)}
                className="inline-block bg-white text-primary hover:bg-gray-100 px-8 py-4 rounded-lg font-semibold transition-all whitespace-nowrap cursor-pointer"
              >
                Start Automating My Marketing
              </button>
            </div>

          </div>
        </section>

        <Footer
          onOpenService={handleOpenService}
          onOpenIndustries={() => setIsIndustriesModalOpen(true)}
          onOpenHowItWorks={() => setIsHowItWorksModalOpen(true)}
        />

        <ServiceModal isOpen={isServiceModalOpen} onClose={() => setIsServiceModalOpen(false)} title={selectedService} />
        <IndustriesModal isOpen={isIndustriesModalOpen} onClose={() => setIsIndustriesModalOpen(false)} />
        <HowItWorksModal isOpen={isHowItWorksModalOpen} onClose={() => setIsHowItWorksModalOpen(false)} />
        <CalendarModal isOpen={isCalendarOpen} onClose={() => setIsCalendarOpen(false)} />
      </div>
    </>
  );
}
